import { useCallback, useEffect, useRef, useState } from 'react'

/** What the form should say about itself right now; null = nothing worth saying. */
export type AutosaveStatus = 'saving' | 'saved' | 'error' | null

export interface Autosave {
  status: AutosaveStatus
  /** Revert the last autosave. Present only while the undo window is open. */
  undo: (() => void) | null
  /** Persist straight away instead of waiting out the pause (for an explicit Save button). */
  flush: () => void
}

/**
 * Saves a form a short pause after the user stops typing, and reports what to show about it.
 *
 * <p>The client's CEO asked for this directly — "it should auto save, but you should be able to
 * undo" — because agents lose work to a Save button they never noticed. Two deliberate limits come
 * from the same conversation: a field the user must confirm (a phone number, an email address) is
 * simply left out of {@code values} and keeps its own Save button, and {@code blocked} holds off
 * while the form is invalid rather than persisting something half-typed.
 *
 * <p>Autosave is additive: an explicit Save still works through {@link Autosave.flush}, so nothing
 * depends on the timer having fired.
 */
export function useAutosave<S>({
  values,
  dirty,
  blocked = false,
  delayMs = 900,
  undoMs = 10_000,
  capture,
  save,
  restore,
}: {
  /** The editable values — any change to these re-arms the pause. */
  values: unknown
  /** Whether those values differ from the saved copy; nothing is sent when they don't. */
  dirty: boolean
  /** Hold off (a validation error): the user is mid-correction, not mid-thought. */
  blocked?: boolean
  delayMs?: number
  undoMs?: number
  /** The saved values as they stand *before* this save — what Undo goes back to. */
  capture: () => S
  /** Persist the current values; call {@code done(true)} on success. */
  save: (done: (ok: boolean) => void) => void
  /** Put a captured snapshot back, both on screen and on the server. */
  restore: (snapshot: S, done: (ok: boolean) => void) => void
}): Autosave {
  const [status, setStatus] = useState<AutosaveStatus>(null)
  const [undoSnapshot, setUndoSnapshot] = useState<S | null>(null)
  // Undoing re-dirties the form on its way back; without this the timer would immediately "redo".
  const undoing = useRef(false)

  // Callbacks are re-created every render by the caller; read them through a ref so the debounce
  // timer below depends on the values alone and isn't restarted by each new closure. Refreshed in an
  // effect (which runs before the debounce effect below) rather than during render.
  const latest = useRef({ capture, save, restore })
  useEffect(() => {
    latest.current = { capture, save, restore }
  })

  const serialised = JSON.stringify(values ?? null)

  useEffect(() => {
    if (!dirty || blocked || undoing.current) return
    const timer = setTimeout(() => {
      const before = latest.current.capture()
      setStatus('saving')
      latest.current.save((ok) => {
        setStatus(ok ? 'saved' : 'error')
        if (ok) setUndoSnapshot(before)
      })
    }, delayMs)
    return () => clearTimeout(timer)
  }, [serialised, dirty, blocked, delayMs])

  // The undo offer is transient — a minute later it would revert an edit the user has forgotten
  // making. The "Saved" label itself stays until the next edit.
  useEffect(() => {
    if (!undoSnapshot) return
    const timer = setTimeout(() => setUndoSnapshot(null), undoMs)
    return () => clearTimeout(timer)
  }, [undoSnapshot, undoMs])

  const undo = useCallback(() => {
    const snapshot = undoSnapshot
    if (!snapshot) return
    undoing.current = true
    setUndoSnapshot(null)
    setStatus('saving')
    latest.current.restore(snapshot, (ok) => {
      undoing.current = false
      setStatus(ok ? 'saved' : 'error')
    })
  }, [undoSnapshot])

  const flush = useCallback(() => {
    setStatus('saving')
    latest.current.save((ok) => setStatus(ok ? 'saved' : 'error'))
  }, [])

  return { status, undo: undoSnapshot ? undo : null, flush }
}
